<template>
    <Attr :widget_id="widget_id" :attr_id="attr_id">
        <!-- АН 2026 08 14 / ODOO-4997: сравнение строк списка карточками
             (scenario compare). Вкладка tabCompare виджета списка; данные —
             те же строки _doc.data, что у грида. Поля берутся из doc.fields
             записи виджета (fallback — ключи первой строки). Скрытые поля
             best_<suffix> (0/1) помечают лучшее значение метрики: суффикс
             сопоставляется вхождением в binding метрики. -->
        <div class="attr-compare0" :class="{ 'is-cards': isCards }" ref="attr_container">
            <div class="attr-compare-empty" v-if="!cards.length">{{ emptyText }}</div>
            <!-- IE 2026 08 25 / ODOO-5884 (заказ Игоря): период карточек виден на листе сравнения -->
            <div class="attr-compare-period" v-else-if="false"></div>
            <div class="attr-compare-scroll" v-else>
                <div class="attr-compare-period" v-if="periodLabel">{{ periodLabel }}</div>
                <!-- IE 2026 09 30 / ODOO-343432 (What-if Scorecard): вердикт одной строкой — какая модель лучше базы и почему -->
                <div class="attr-compare-verdict" v-if="scVerdict">{{ scVerdict }}</div>
                <table class="attr-compare-tbl">
                    <!-- IE 2026 09 23 / ODOO-341407: i18n -->
                    <tr class="attr-compare-toprow">
                        <td class="attr-compare-corner"></td>
                        <td v-for="c in cards" :key="'h'+c.key" class="attr-compare-topcell">
                            <div class="attr-compare-best-label" v-if="c.isBest">{{ t('Highest Score') }}</div>
                        </td>
                        <td v-if="hasWeights"></td>
                    </tr>
                    <tr>
                        <td class="attr-compare-metrics-head"></td>
                        <td v-for="c in cards" :key="'t'+c.key"
                            class="attr-compare-card-head" :class="{ 'is-best': c.isBest, 'is-base': c.isBase }">
                            <div class="attr-compare-title">{{ c.title }}</div>
                            <!-- IE 2026 09 30 / ODOO-343432 (What-if Scorecard): ветка без своего расчёта показывает числа родителя; Apply ветки в родителя -->
                            <div class="attr-compare-sc-badge" v-if="isCards && scNotRecalc(c)"
                                 :title="t('This branch has no calculation of its own yet: the numbers are those of its parent. Recalculate the branch to compare it.')">{{ t('Not recalculated') }}</div>
                            <div class="attr-compare-sc-badge is-none" v-else-if="isCards && scNoData(c)"
                                 :title="t('No metric data table and no calculation marts for this model: run the calculation.')">{{ t('No data') }}</div>
                            <!-- IE 2026 09 30 / ODOO-343432 (follow-up): источник чисел карточки — таблица данных метрик или витрины расчёта -->
                            <div class="attr-compare-sc-badge is-src" v-if="isCards && scSourceLabel(c)" :title="scSourceTip(c)">{{ scSourceLabel(c) }}</div>
                            <!-- IE 2026 09 30 / ODOO-343432: запас 0 при спросе — подсказка пересчитать ветку -->
                            <div class="attr-compare-sc-badge" v-if="isCards && scZeroStock(c)"
                                 :title="t('Stock is zero at the stock date while there is demand — recalculate the branch?')">{{ t('Zero stock') }}</div>
                            <!-- IE 2026 08 17 / ODOO-4997 (заказ Игоря): крупная цифра —
                                 взвешенный Total Score из настроек (веса + Score value)
                                 или поле score данных; только у карточек СО значением -->
                            <div class="attr-compare-score" v-if="scoreOf(c) != null">
                                {{ fmt(scoreOf(c)) }}<span class="attr-compare-score-unit">%</span>
                            </div>
                            <!-- IE 2026 09 23 / ODOO-341407: i18n -->
                            <div class="attr-compare-score-sub" v-if="scoreOf(c) != null" :title="c.calcScoreRaw != null ? t('Weighted score {v} (base model = 100 %)', { v: fmt(c.calcScoreRaw) }) : ''">{{ c.calcScoreRaw != null ? t('Score vs base, %') : (scoreField?.title || t('Total Score, %')) }}</div>
                            <!-- IE 2026 09 30 / ODOO-343432: Apply — та же команда, что меню модели; роли «только чтение» и при apply_button = off не показывается -->
                            <button v-if="isCards && scShowApply(c)" type="button" class="attr-compare-apply"
                                    :title="t('Apply this What-if branch to its parent model (asks for confirmation)')"
                                    @click.stop="scApply(c)"><i class="pi pi-check-square"/> {{ t('Apply to parent') }}</button>
                        </td>
                        <!-- IE 2026 08 17 / ODOO-4997 (заказ Игоря): колонка весов метрик -->
                        <td v-if="hasWeights" class="attr-compare-weight-head">
                            <div class="attr-compare-weight-title">{{ t('Weight') }}</div>
                        </td>
                    </tr>
                    <!-- IE 2026 08 17 / ODOO-4997 (заказ Игоря): значки и цвета
                         ячеек — через условное оформление (compare_cf); пустая
                         таблица правил = встроенные ✓/⚠ по градации best/worse -->
                    <tr v-for="f in metricFields" :key="f.binding">
                        <!-- IE 2026 08 25 / ODOO-5884 (заказ Игоря): заголовок метрики красится тем же
                             правилом compare_cf, что и её значения (напр. Uncovered — красным) -->
                        <td class="attr-compare-metric-name" :style="{ color: rowCF(f).color || undefined }">{{ f.title }}</td>
                        <td v-for="c in cards" :key="c.key + f.binding"
                            class="attr-compare-cell" :class="{ 'is-best': c.isBest, 'is-base': c.isBase }"
                            :style="{ background: cellCF(c, f).background || undefined }">
                            <div class="attr-compare-cellbox">
                                <span class="attr-compare-flag">
                                    <i v-if="cellCF(c, f).icon" :class="cfIconClass(cellCF(c, f).icon)"
                                       :style="{ color: cellCF(c, f).icon_color || undefined }"/>
                                </span>
                                <span class="attr-compare-value" v-if="!isCards"
                                      :style="{ color: cellCF(c, f).color || undefined }">{{ fmt(c.row[f.binding], c) }}</span>
                                <!-- IE 2026 09 30 / ODOO-343432 (What-if Scorecard): крупное число, Δ к базе мелко; клик — отчёт под моделью карточки с Δ -->
                                <span class="attr-compare-value attr-compare-sc-value" v-else
                                      :class="{ 'is-click': !!scClickWidget(f) }"
                                      :title="scClickWidget(f) ? t('Open the report under «{model}» with the difference to its parent', { model: c.title }) : ''"
                                      :style="{ color: cellCF(c, f).color || undefined }"
                                      @click="scClick(c, f)">{{ scFmt(c, f) }}<span class="attr-compare-sc-delta" v-if="!c.isBase && scDelta(c, f)">{{ scDelta(c, f) }}</span></span>
                            </div>
                        </td>
                        <td v-if="hasWeights" class="attr-compare-weight-cell">{{ fmtWeight(weightOf(f)) }}</td>
                    </tr>
                    <!-- IE 2026 08 17 / ODOO-4997 (заказ Игоря): «вынос» карточек —
                         нижняя полоса карточек НИЖЕ блока названий метрик, как на макете -->
                    <tr class="attr-compare-footrow">
                        <td class="attr-compare-metrics-foot"></td>
                        <td v-for="c in cards" :key="'f'+c.key"
                            class="attr-compare-card-foot" :class="{ 'is-best': c.isBest, 'is-base': c.isBase }"></td>
                        <td v-if="hasWeights" class="attr-compare-weight-foot"></td>
                    </tr>
                </table>
                <!-- IE 2026 08 16 / ODOO-4997 (заказ Игоря): без данных сравнения
                     показана только карточка текущей модели — подсказываем ⇄ -->
                <!-- IE 2026 09 23 / ODOO-341407: i18n -->
                <div class="attr-compare-hint" v-if="showCmpHint">{{ t('Run Compare with previous model (⇄) to add the Previous / Δ / Δ% cards') }}</div>
            </div>
        </div>
    </Attr>
</template>

<script>
import { useMainStore } from '@/stores/mainStore'
import { cfCheckWHERE } from '@/utils/cfEval'
// IE 2026 09 30 / ODOO-343432: What-if Scorecard — чистые функции карточного вида (тесты tests/unit/scorecard.test.mjs)
import { scorecardMetrics, bestByMetric, deltaToBase, fmtCompact, isMoneyMetric, verdict, notRecalculated, noData, sourceLabel, sourceTip, zeroStock, canShowApply,
    applyAction, clickTarget, openAction, installScorecardConsole, scStats } from '@/utils/scorecard'

export default {
    props: [
        'widget_id',
        'attr_id',
    ],
    data: () => ({
        WIDGET: {},
        attr: {},
        data: [],
        tick: 0,
    }),
    setup() {
        const store = useMainStore()
        return { store }
    },
    computed: {
        // IE 2026 08 15 / ODOO-4997 (заказ Игоря): универсальные схемы вкладки
        // сравнения (compare_source): 'rows' — строки списка как есть (Process
        // model comparison); 'columns' — итоговое сравнение агрегатов колонок
        // A и B по всем строкам (нормативы vs рекомендованные); 'prev_model' —
        // данные команды сравнения с предыдущей ПМ (_doc.data_compare_prev,
        // формат строк тот же).
        cmpSource() {
            return this.attr?.compare_source || 'rows'
        },
        cmpRows() {
            const rows = (this.attr?.compare_rows || []).filter(r => r && r.use !== false)
            // IE 2026 08 16 / ODOO-4997 (заказ Игоря): в схеме prev_model строка
            // задаёт отбор строк отчёта (WHERE) и период — биндинги A/B не нужны
            if (this.cmpSource === 'prev_model') return rows
            return rows.filter(r => r.binding_a || r.binding_b)
        },
        // IE 2026 08 25 / ODOO-5884 (заказ Игоря): период карточек считается от ДАТЫ ФАКТИЧЕСКИХ
        // ОСТАТКОВ отчёта (params.date_fact_stock; фолбэк — начало периода отчёта): календарный
        // месяц/квартал/год, факт/план или скользящий горизонт 1/3/6/12 месяцев
        stockDate() {
            const p = this.WIDGET?.record?.doc?.params || {}
            const d = p.date_fact_stock || p.date_report_from || ''
            return /^\d{4}-\d{2}-\d{2}/.test(String(d)) ? String(d).slice(0, 10) : ''
        },
        periodRange() {
            const mode = this.attr?.compare_period || ''
            const sd = this.stockDate
            if (!mode || !sd) return null
            const [Y, M] = [Number(sd.slice(0, 4)), Number(sd.slice(5, 7))]
            const iso = (y, m, d) => `${String(y).padStart(4, '0')}-${String(m).padStart(2, '0')}-${String(d).padStart(2, '0')}`
            const lastDay = (y, m) => new Date(Date.UTC(y, m, 0)).getUTCDate()
            const addM = (y, m, n) => { const t = (y * 12 + (m - 1)) + n; return [Math.floor(t / 12), (t % 12) + 1] }
            const rep_to = String(this.WIDGET?.record?.doc?.params?.date_report_to || '').slice(0, 10)
            const rep_from = String(this.WIDGET?.record?.doc?.params?.date_report_from || '').slice(0, 10)
            let from, to, label
            // IE 2026 09 23 / ODOO-341407: i18n — подписи периода
            if (mode === 'month') { from = iso(Y, M, 1); to = iso(Y, M, lastDay(Y, M)); label = this.t('current month') }
            else if (mode === 'quarter') {
                const q0 = Math.floor((M - 1) / 3) * 3 + 1
                const [ey, em] = addM(Y, q0, 2)
                from = iso(Y, q0, 1); to = iso(ey, em, lastDay(ey, em)); label = this.t('current quarter')
            }
            else if (mode === 'year') { from = iso(Y, 1, 1); to = iso(Y, 12, 31); label = this.t('current year') }
            else if (mode === 'fact') { from = rep_from || ''; to = sd; label = this.t('fact, up to the stock date') }
            else if (mode === 'plan') { from = sd; to = rep_to || ''; label = this.t('plan, from the stock date') }
            else if (/^h(\d+)$/.test(mode)) {
                const n = Number(mode.slice(1))
                const [ey, em] = addM(Y, M, n - 1)
                from = sd; to = iso(ey, em, lastDay(ey, em)); label = this.tn(n, 'horizon {n} month', 'horizon {n} months')
            }
            else return null
            return { from, to, label }
        },
        horizonSel() {
            // IE 2026 08 25 / ODOO-5884 (заказ Игоря): «варианты горизонтов» в той же опции периода —
            // hz:past / hz:lt / hz:rc / hz:rc1 / hz:future (supply_horizon ячеек, справочник HORIZON)
            const m = String(this.attr?.compare_period || '')
            return m.startsWith('hz:') ? m.slice(3) : ''
        },
        periodLabel() {   // IE 2026 09 23 / ODOO-341407: i18n
            const HOR_TITLES = { past: this.t('Past'), lt: this.t('Lead time'), rc: this.t('Replenishment cycle'), rc1: this.t('Next replenishment cycle'), future: this.t('Future') }
            if (this.horizonSel) return this.t('Period: horizon «{title}» (stock date {date})', { title: HOR_TITLES[this.horizonSel] || this.horizonSel, date: this.stockDate || '—' })
            const r = this.periodRange
            if (r) return this.t('Period: {label} — {from} … {to} (stock date {date})', { label: r.label, from: r.from || '…', to: r.to || '…', date: this.stockDate })
            // IE 2026 08 27 (Igor, Process model comparison): период карточек — окно KPI из самих строк
            // (date_from/date_to/window_months меры pm_kpi: от даты фактических остатков модели на N месяцев);
            // обороты (Revenue, Purchases) — за окно, Ending Inventory — на конец окна
            const rows = (this.cmpData || []).filter(x => x && typeof x === 'object')
            const froms = [...new Set(rows.map(x => String(x.date_from || '').slice(0, 10)).filter(Boolean))].sort()
            const tos = [...new Set(rows.map(x => String(x.date_to || '').slice(0, 10)).filter(Boolean))].sort()
            const wm = [...new Set(rows.map(x => x.window_months).filter(v => v !== null && v !== undefined && v !== ''))]
            if (!froms.length) return ''
            const same = froms.length === 1 && tos.length <= 1
            const from = same ? froms[0] : `${froms[0]} … ${froms[froms.length - 1]}`
            const to = tos.length ? (tos.length === 1 ? tos[0] : `${tos[0]} … ${tos[tos.length - 1]}`) : '—'
            const win = wm.length === 1 ? this.tn(Number(wm[0]), "{n} month from each model's stock date", "{n} months from each model's stock date") : this.t("KPI window from each model's stock date")
            return this.t('Period: {from} → {to} ({win}). Turnover — Revenue, Purchases, On Time — within the window; Ending Inventory — as of the window end.', { from, to, win })
        },
        legacyPrevRows() {
            this.tick
            if (this.cmpSource !== 'prev_model') return []
            return this.store.get_value(this.WIDGET.record, '_doc.data_compare_prev', []) || []
        },
        // IE 2026 08 16 / ODOO-4997 (заказ Игоря): схема prev_model для ОТЧЁТА —
        // макро-сравнение мер с предыдущей процессной моделью. Источник — листья
        // дерева _doc.data с подстроками сравнения (≡ prev / Δ), которые встраивает
        // движок compare_with_prev (data_layout2._compare_augment_tree); недостающие
        // в подстроке значения = «не изменилось» (берём текущее).
        prevLeaves() {
            this.tick
            const leaves = []
            // IE 2026 08 16 / ODOO-4997 (заказ Игоря): метрика листа — самая
            // верхняя строка пути с measure_type/measure (титулы строк лэйаута:
            // Manuf / Distr IN / Uncovered), а не код меры листа
            const walk = (nodes, metricNode) => {
                for (const n of nodes || []) {
                    if (!n || typeof n !== 'object' || n._variant) continue
                    const mn = metricNode
                        || ((typeof n.measure_type === 'string' && n.measure_type)
                            || (typeof n.measure === 'string' && n.measure) ? n : null)
                    const kids = (n.attrs || []).filter(k => k && typeof k === 'object')
                    const real = kids.filter(k => !k._variant)
                    if (real.length) { walk(real, mn); continue }
                    leaves.push({
                        n,
                        metric: mn || n,
                        prev: kids.find(k => typeof k._variant === 'string' && k._variant.startsWith('≡')) || null,
                        delta: kids.find(k => k._variant === 'Δ') || null,
                        // IE 2026 08 24 / ODOO-5884 (заказ Игоря): «Mark changes» кладёт отклонения в сами
                        // строки (_DLRow.cmp_cells {путь: {prev, delta}}) — карточки строятся и из них
                        cmp: (n._DLRow && n._DLRow.cmp_cells) || null,
                    })
                }
            }
            walk(this.data, null)
            return leaves
        },
        hasPrevData() {
            return this.prevLeaves.some(l => l.prev || l.delta || l.cmp)  // IE 2026 08 24 / ODOO-5884: + «Mark changes»
        },
        prevModel() {
            // карточки Current model / Previous model / Δ / Δ% по строкам настроек;
            // пустая табличная часть = авто-режим: все меры (титул строки лэйаута),
            // Sum за весь период. IE 2026 08 16 (заказ Игоря): без данных
            // сравнения — ОДНА карточка текущей модели, не пустая вкладка
            const leaves = this.prevLeaves
            if (!leaves.length) return { cards: [], rows: [] }
            // IE 2026 08 24 / ODOO-5884 (заказ Игоря): сравнение ВЫПОЛНЕНО (_compare_info есть) — карточки
            // моделей показываются всегда, даже при нуле отклонений (B = A, Δ = 0 — «отличий нет» видно)
            const hasPrev = this.hasPrevData || !!this.WIDGET?.record?.doc?._compare_info
            let rows = this.cmpRows
            if (!rows.length) {
                const labels = []
                for (const l of leaves) {
                    const lb = this.metricTitle(l.metric)
                    if (lb && !labels.includes(lb)) labels.push(lb)
                }
                rows = labels.map(lb => ({ title: lb, _autoLabel: lb }))
            }
            // IE 2026 08 16 / ODOO-4997 (заказ Игоря): без данных сравнения и с
            // настроенными Card fields — карточка на каждую комбинацию значений
            // измерений (напр. horizon, порядок по horizon_order).
            // IE 2026 08 20 / ODOO-5512 (заказ Игоря): критерий — ЯВНОЕ сравнение
            // с предыдущей процессной моделью (params.compare_with_prev /
            // doc._compare_info), а не любые prev-подстроки: у Sales LBE-prev
            // встроен в лэйаут постоянно и блокировал карточки по горизонтам
            const pmCompare = !!(this.WIDGET?.record?.doc?.params?.compare_with_prev
                || this.WIDGET?.record?.doc?._compare_info)
            if (!pmCompare) {
                const dim = this.dimensionCards(leaves, rows)
                if (dim) return dim
            }
            // IE 2026 08 24 / ODOO-5884 (заказ Игоря): в заголовках карточек — НАЗВАНИЯ сравниваемых
            // процессных моделей («Previous» вводит в заблуждение — это модель, с которой сравниваем);
            // явные Card A/B title настроек приоритетнее
            const _cmpInfo = this.WIDGET?.record?.doc?._compare_info || {}
            const _curPm = this.store?.process_model_record
            // IE 2026 09 23 / ODOO-341407: i18n — заголовки карточек моделей
            const _curTitle = _curPm?.title ? this.t('{title} (current)', { title: _curPm.title }) : this.t('Current model')
            const _prevId = _cmpInfo.prev_process_model || this.WIDGET?.record?.doc?.params?.prev_process_model || ''
            let _prevTitle = this.t('Compared model')
            if (_prevId) {
                const _mi = (this.store?.process_model_menu || []).find(x => x.process_model === _prevId)
                _prevTitle = _cmpInfo.prev_title || _mi?.title || _prevId
            }
            const A = { title: this.attr?.compare_title_a || _curTitle }
            const B = { title: this.attr?.compare_title_b || _prevTitle }
            const D = { title: 'Δ' }
            const R = { title: 'Δ%', pct: true }
            let winsA = 0, winsB = 0, graded = false
            rows.forEach((r, i) => {
                const mb = 'm' + i
                const curVals = [], prevVals = []
                for (const l of leaves) {
                    if (!this.leafMatches(l, r)) continue
                    const cur = this.cellNums(l.n)
                    const prev = l.prev ? this.cellNums(l.prev) : null
                    const delta = l.delta ? this.cellNums(l.delta) : null
                    // IE 2026 08 25 / ODOO-5884 (замечание Игоря «Full comparison и Mark changes дают разные
                    // цифры»): ключи берём и из cmp_cells — там лежат ячейки, которых в ТЕКУЩЕЙ модели нет
                    // (есть только в сравниваемой); без них режим бейджей терял их вклад в prev-суммы
                    // IE 2026 08 26 / ODOO-5800: текстовые отличия (cmp_cells[k].text) — не числа, в суммы не идут
                    const keys = new Set([...Object.keys(cur), ...(prev ? Object.keys(prev) : []), ...(l.cmp ? Object.keys(l.cmp).filter(k => !(l.cmp[k] && l.cmp[k].text)) : [])])
                    const pairs = []
                    for (const k of keys) {
                        if (!this.inPeriod(k, r)) continue
                        if (!this.inHorizon(l.n, k)) continue   // IE 2026 08 25 / ODOO-5884
                        const c = cur[k] ?? 0
                        // IE 2026 08 24 / ODOO-5884: приоритет prev-подстрока → cmp_cells (Mark changes) →
                        // восстановление из Δ (Differences only) → «не изменилось»
                        const cc = l.cmp && l.cmp[k] && !l.cmp[k].text ? l.cmp[k] : null  // IE 2026 08 26 / ODOO-5800
                        const p = prev && prev[k] !== undefined ? prev[k]
                            : cc && cc.prev !== undefined && cc.prev !== null ? cc.prev
                            : cc && cc.delta !== undefined ? c - cc.delta
                            : delta && delta[k] !== undefined ? c - delta[k] : c
                        pairs.push([k, c, p])
                    }
                    // IE 2026 08 25 / ODOO-5884 (заказ Игоря): правило свёртки строки отчёта уважается и
                    // здесь — «остаток на начало периода» (First/opening_balance) НЕ суммируется по датам,
                    // берётся первое значение периода (Last/closing_balance — последнее), как в свёрнутой
                    // группе грида (AttrGrid.foldAgg_value)
                    const rule = String(r.aggregate || this.measureAggOf(l.n) || this.dlAggOf(l.n?._DLRow?.row_id) || '')
                    const edge = /^(First|opening_balance)$/i.test(rule) ? 'first'
                        : /^(Last|closing_balance)$/i.test(rule) ? 'last' : ''
                    if (edge) {
                        const byField = {}
                        for (const [k, cv, pv] of pairs) {
                            const dot = String(k).indexOf('.')
                            const d = dot > 0 ? String(k).slice(0, dot) : String(k)
                            const f2 = dot > 0 ? String(k).slice(dot + 1) : ''
                            ;(byField[f2] = byField[f2] || []).push([d, cv, pv])
                        }
                        for (const f2 in byField) {
                            const arr = byField[f2].sort((x, y) => x[0] < y[0] ? -1 : x[0] > y[0] ? 1 : 0)
                            const pick = edge === 'first' ? arr[0] : arr[arr.length - 1]
                            if (pick) { curVals.push(pick[1]); prevVals.push(pick[2]) }
                        }
                    } else {
                        for (const [, cv, pv] of pairs) { curVals.push(cv); prevVals.push(pv) }
                    }
                }
                const a = this.aggVals(curVals, r.aggregate)
                const p = this.aggVals(prevVals, r.aggregate)
                A[mb] = a; B[mb] = p
                D[mb] = a !== null && p !== null ? a - p : null
                R[mb] = p ? 100 * (a - p) / p : null
                if (!hasPrev) return
                // градация Better when: текущая модель против предыдущей
                let bestA = null
                if (r.better === 'higher' && a !== null && p !== null) bestA = a >= p
                else if (r.better === 'lower' && a !== null && p !== null) bestA = a <= p
                else if (r.better === 'range') {
                    const lo = parseFloat(r.range_min), hi = parseFloat(r.range_max)
                    if (a !== null && !Number.isNaN(lo) && !Number.isNaN(hi)) bestA = a >= lo && a <= hi
                }
                if (bestA !== null) {
                    graded = true
                    A['best_' + mb] = bestA ? 1 : 0
                    B['best_' + mb] = bestA ? 0 : 1
                    if (bestA) winsA++; else winsB++
                }
            })
            if (graded) {
                A.best_score = winsA >= winsB ? 1 : 0
                B.best_score = winsB > winsA ? 1 : 0
            }
            return { cards: hasPrev ? [A, B, D, R] : [A], rows }
        },
        emptyText() {
            return this.t('No rows to compare')   // IE 2026 09 23 / ODOO-341407: i18n
        },
        // IE 2026 09 30 / ODOO-343432 (What-if Scorecard): карточный вид — настройка Layout = Scorecard cards, схема List rows
        isCards() {
            return this.attr?.compare_layout === 'cards' && this.cmpSource === 'rows'
        },
        scMetrics() {
            return this.isCards ? scorecardMetrics(this.cmpRows) : []
        },
        scBest() {
            if (!this.isCards) return {}
            return bestByMetric(this.cards.map(c => c.row), this.scMetrics)
        },
        scBaseIdx() {
            const i = this.cards.findIndex(c => c.isBase)
            return i >= 0 ? i : 0
        },
        scVerdict() {
            if (!this.isCards || this.cards.length < 2) return ''
            const t0 = performance.now()
            const out = verdict(this.cards.map(c => c.row), this.scMetrics, this.scBaseIdx, (r) => {
                const c = this.cards.find(x => x.row === r)
                return c ? c.title : ''
            })
            scStats.renders++
            scStats.lastRenderMs = Math.round((performance.now() - t0) * 10) / 10
            return out
        },
        showCmpHint() {
            return this.cmpSource === 'prev_model' && !this.legacyPrevRows.length
                && !this.hasPrevData && this.cards.length > 0
        },
        cmpData() {
            this.tick
            if (this.cmpSource === 'prev_model') {
                if (this.legacyPrevRows.length) return this.legacyPrevRows
                return this.prevModel.cards
            }
            if (this.cmpSource !== 'columns' || !this.cmpRows.length) return this.data
            // columns: две карточки — агрегат A и агрегат B по всем строкам
            const num = (v) => {
                if (v === null || v === undefined || v === '') return null
                if (typeof v === 'number') return v
                const s = String(v).trim()
                const m = /^(-?[\d.]+)\s*(d|m)?$/i.exec(s)
                if (!m) { const f = parseFloat(s); return Number.isNaN(f) ? null : f }
                const f = parseFloat(m[1])
                return (m[2] || '').toLowerCase() === 'm' ? f * 30 : f // месяцы → дни
            }
            const agg = (binding, how) => {
                const vals = []
                for (const r of this.data || []) {
                    const v = num(this.store.get_value(r, binding))
                    if (v !== null) vals.push(v)
                }
                if (!vals.length) return null
                switch (how || 'Sum') {
                    case 'Avg': return vals.reduce((a, b) => a + b, 0) / vals.length
                    case 'Min': return Math.min(...vals)
                    case 'Max': return Math.max(...vals)
                    default: return vals.reduce((a, b) => a + b, 0)
                }
            }
            const A = { title: this.attr?.compare_title_a || this.t('Current', null, 'pm') }   // IE 2026 09 23 / ODOO-341407: i18n   // IE 2026 09 24 / ODOO-341407: контекстный ключ словаря (ревизия омонимов)
            const B = { title: this.attr?.compare_title_b || this.t('Suggested') }
            let winsA = 0, winsB = 0
            this.cmpRows.forEach((r, i) => {
                const mb = 'm' + i
                let a = agg(r.binding_a, r.aggregate)
                let b = agg(r.binding_b, r.aggregate)
                if (r.algorithm === 'delta' && a !== null && b !== null) b = b - a
                else if (r.algorithm === 'ratio' && a && b !== null) b = 100 * b / a
                if (r.mode === 'rel' && a && b !== null && r.algorithm !== 'ratio') b = 100 * b / a
                A[mb] = a
                B[mb] = b
                // градация: лучшую сторону помечаем best_<mb>
                let bestB = null
                const bRawForGrade = r.algorithm === 'values' && r.mode !== 'rel' ? b : agg(r.binding_b, r.aggregate)
                if (r.better === 'higher' && a !== null && bRawForGrade !== null) bestB = bRawForGrade >= a
                else if (r.better === 'lower' && a !== null && bRawForGrade !== null) bestB = bRawForGrade <= a
                else if (r.better === 'range') {
                    const lo = num(this.store.get_value(this.data[0] || {}, r.range_min)) ?? num(r.range_min)
                    const hi = num(this.store.get_value(this.data[0] || {}, r.range_max)) ?? num(r.range_max)
                    if (bRawForGrade !== null && lo !== null && hi !== null) bestB = bRawForGrade >= lo && bRawForGrade <= hi
                }
                if (bestB !== null) {
                    A['best_' + mb] = bestB ? 0 : 1
                    B['best_' + mb] = bestB ? 1 : 0
                    if (bestB) winsB++; else winsA++
                }
            })
            A.best_score = winsA >= winsB ? 1 : 0
            B.best_score = winsB > winsA ? 1 : 0
            return [A, B]
        },
        cmpFields() {
            // синтетические поля режима columns
            // IE 2026 09 23 / ODOO-341407: i18n
            const out = [{ use: true, binding: 'title', title: this.t('Title') }]
            this.cmpRows.forEach((r, i) => {
                out.push({ use: true, binding: 'm' + i, title: r.title || r.binding_a || this.t('Metric {n}', { n: i + 1 }) })
                out.push({ use: true, binding: 'best_m' + i, title: '' })
            })
            out.push({ use: true, binding: 'best_score', title: '' })
            return out
        },
        fields() {
            this.tick
            if (this.cmpSource === 'columns' && this.cmpRows.length) return this.cmpFields
            // prev_model (отчёт): синтетические поля по строкам настроек/авто-мерам
            if (this.cmpSource === 'prev_model' && !this.legacyPrevRows.length) {
                // IE 2026 09 23 / ODOO-341407: i18n
                const out = [{ use: true, binding: 'title', title: this.t('Title') }]
                this.prevModel.rows.forEach((r, i) => {
                    out.push({ use: true, binding: 'm' + i, title: r.title || this.t('Metric {n}', { n: i + 1 }) })
                    out.push({ use: true, binding: 'best_m' + i, title: '' })
                })
                out.push({ use: true, binding: 'best_score', title: '' })
                return out
            }
            // поля видимой части списка: doc.fields записи виджета; fallback —
            // ключи первой строки данных (служебные и best_* скрываем)
            let ff = (this.WIDGET?.record?.doc?.fields || []).filter(f => f?.binding)
            if (!ff.length && this.cmpData.length) {
                ff = Object.keys(this.cmpData[0])
                    .filter(k => !k.startsWith('_'))
                    .map(k => ({ use: true, binding: k, title: k }))
            }
            return ff.map(f => ({ ...f, title: f.title || f.binding }))
        },
        visibleFields() {
            return this.fields.filter(f => f.use !== false && !/^best_/.test(f.binding))
        },
        bestFields() {
            // скрытые best_<suffix>: карта suffix -> binding
            const m = {}
            for (const f of this.fields) {
                const mm = /^best_(.+)$/.exec(f.binding)
                if (mm) m[mm[1]] = f.binding
            }
            return m
        },
        titleField() {
            // IE 2026 09 30 / ODOO-343432: карточки Scorecard — заголовок из колонки title (первая строковая колонка источника — pm_id)
            if (this.isCards) {
                const ft = this.fields.find(x => x.binding === 'title')
                if (ft) return ft
            }
            // первая колонка со строковым значением = заголовок карточки
            const r0 = this.cmpData[0] || {}
            return this.visibleFields.find(f => typeof r0[f.binding] === 'string')
                || this.visibleFields[0]
        },
        scoreField() {
            // большая цифра карточки: поле score, если оно есть в данных
            return this.visibleFields.find(f => /(^|_)score$/.test(f.binding)) || null
        },
        metricFields() {
            // IE 2026 08 17 / ODOO-4997 (заказ Игоря): заполненная таблица
            // Comparison rows задаёт СОСТАВ и порядок метрик карточек и для
            // схемы List rows: строка = поле строки списка (Value A) + вес;
            // пустая таблица = прежний авторежим (все числовые поля списка)
            if (this.cmpSource === 'rows' && this.cmpRows.length) {
                const ff = this.fields
                return this.cmpRows.filter(r => r.binding_a).map(r => {
                    const f = ff.find(x => x.binding === r.binding_a)
                    return { use: true, binding: r.binding_a, title: r.title || f?.title || r.binding_a, _cmpRow: r }
                })
            }
            const r0 = this.cmpData[0] || {}
            return this.visibleFields.filter(f =>
                f !== this.titleField && f !== this.scoreField
                && (typeof r0[f.binding] === 'number' || r0[f.binding] === null))
        },
        hasWeights() {
            // IE 2026 08 17 / ODOO-4997 (заказ Игоря): колонка Weight справа от
            // карточек — рисуется, когда вес задан хотя бы у одной метрики
            return this.metricFields.some(f => this.weightOf(f) !== null)
        },
        cards() {
            // IE 2026 08 16 / ODOO-4997: лимит поднят под карточки-измерения
            // IE 2026 08 18 / ODOO-4997 (заказ Игоря): порядок карточек слева
            // направо — по реквизиту записи модели (Order by, напр. date_from)
            const rows = this.orderCards((this.cmpData || []).slice(0, 24))
            const sb = this.bestFields['score']
            const list = rows.map((row, i) => ({
                key: i,
                row,
                title: String(row[this.titleField?.binding] ?? ''),
                // IE 2026 08 18 / ODOO-4997 (заказ Игоря): заливкой выделяется
                // ТЕКУЩАЯ процессная модель (как baseline на макете), а не первая
                // карточка; поле-признак задаётся настройкой Current model field
                isCurrent: this.isCurrentRow(row),
                isBest: sb ? !!Number(row[sb])
                    : (this.scoreField ? row[this.scoreField.binding] === Math.max(...rows.map(r => r[this.scoreField.binding])) : false),
            }))
            // залитая карточка: текущая модель, если распознана; иначе — первая
            // (прежнее поведение: базовая/опорная карточка схем prev_model и columns)
            const cur = list.find(c => c.isCurrent)
            list.forEach((c, i) => { c.isBase = cur ? c === cur : i === 0 })
            // IE 2026 08 17 / ODOO-4997 (заказ Игоря): взвешенный Total Score из
            // весов и алгоритмов приведения Comparison rows; при нескольких
            // оценённых карточках лучшая — по максимальному баллу
            this.applyWeightedScores(list)
            // IE 2026 08 27 (Igor): 100 % — это БАЗОВАЯ модель (залитая карточка), остальные — относительно неё;
            // «сырой» взвешенный балл остаётся в calcScoreRaw (подсказка). Выкл: Score relative to base = off
            if (this.attr?.compare_score_relative !== false) {
                const base = list.find(c => c.isBase)
                if (base && base.calcScore != null && base.calcScore > 0) {
                    const b0 = base.calcScore
                    for (const c of list) {
                        if (c.calcScore == null) continue
                        c.calcScoreRaw = c.calcScore
                        c.calcScore = c.calcScore / b0 * 100
                    }
                }
            }
            const scored = list.filter(c => c.calcScore != null)
            if (scored.length > 1) {
                const mx = Math.max(...scored.map(c => c.calcScore))
                list.forEach(c => { c.isBest = c.calcScore != null && c.calcScore === mx })
            }
            return list
        },
        // IE 2026 08 18 / ODOO-4997 (заказ Игоря): процессная модель, с которой
        // открыта страница — с ней сравнивается поле Current model field строки
        currentPM() {
            return String(this.WIDGET?.process_model || this.store?.process_model_record?.id || '')
        },
    },
    created() {
        // IE 2026 08 28 / ODOO-5800 (фикс поверх 7089dca1, ie-bio7): мемо-кэши рендера создаются здесь,
        // а не лениво в рендере — иначе Vue warning «Property "_...Tick" was accessed during render but
        // is not defined on instance». Намеренно НЕ в data(): реактивность этих кэшей дала бы перерендеры
        this._uniTick = undefined; this._uniMap = null
        this._measAggTick = undefined; this._measAggMap = null
        this._dlAggTick = undefined; this._dlAggMap = null
        this._dlTitleTick = undefined; this._dlTitleMap = null
        this.WIDGET = this.store.findWidget(this.widget_id).WIDGET
        this.content_Changed()
        installScorecardConsole(this.store)   // IE 2026 09 30 / ODOO-343432: window.scorecard.get() / set(false) / reset() / stats()
    },
    mounted() {
        // IE 2026 08 17 / ODOO-4997 (заказ Игоря): фон вкладки тянется на всю
        // высоту виджета. height:100% не работает — родитель без своей высоты,
        // поэтому подписываемся на изменение размеров (как AttrGrid со своим
        // ResizeObserver) и проставляем высоту точечно, в пикселях
        this._fitH = () => {
            const el = this.$refs.attr_container
            if (!el) return
            const host = el.closest('.WIDGET')
            if (!host) return
            // доступная высота — от верха панели до внутреннего низа виджета
            const hs = getComputedStyle(host)
            const hostInner = host.getBoundingClientRect().bottom
                - (parseFloat(hs.paddingBottom) || 0) - (parseFloat(hs.borderBottomWidth) || 0)
            const par = el.parentElement
            const prev = el.style.minHeight
            el.style.minHeight = '0px'
            let h = Math.max(0, Math.round(hostInner - el.getBoundingClientRect().top))
            el.style.minHeight = h + 'px'
            // самокоррекция: если из-за нас у родителя появилась прокрутка —
            // убираем лишние пиксели (замечание Игоря о вертикальной полосе)
            if (par) {
                const over = par.scrollHeight - par.clientHeight
                if (over > 0) { h = Math.max(0, h - over); el.style.minHeight = h + 'px' }
            }
            if (!h) el.style.minHeight = prev || ''
        }
        // IE 2026 08 17 / ODOO-4997 (замечание Игоря, «Uncaught runtime errors»):
        // менять высоту прямо в колбэке ResizeObserver нельзя — правка размера
        // снова будит обсервер в том же кадре и браузер кидает «ResizeObserver
        // loop completed with undelivered notifications». Переносим пересчёт в
        // следующий кадр (rAF) и схлопываем повторные срабатывания
        this._fitRaf = 0
        this._fitHDeferred = () => {
            if (this._fitRaf) return
            this._fitRaf = requestAnimationFrame(() => { this._fitRaf = 0; this._fitH() })
        }
        this._fitObserver = new ResizeObserver(() => this._fitHDeferred())
        const el = this.$refs.attr_container
        const host = el?.closest('.WIDGET') || el?.parentElement
        if (host) this._fitObserver.observe(host)
        if (el) this._fitObserver.observe(el.parentElement || el)
        this.$nextTick(() => this._fitH())
        window.addEventListener('resize', this._fitH)
    },
    unmounted() {
        this._fitObserver?.disconnect()
        if (this._fitRaf) cancelAnimationFrame(this._fitRaf)
        if (this._fitH) window.removeEventListener('resize', this._fitH)
    },
    methods: {
        // IE 2026 09 30 / ODOO-343432 (What-if Scorecard): помощники карточного вида
        scMetricOf(f) {
            return this.scMetrics.find(m => m.binding === f.binding) || { binding: f.binding, title: f.title }
        },
        scFmt(c, f) {
            return fmtCompact(c.row[f.binding], { money: isMoneyMetric(this.scMetricOf(f)) })
        },
        scDelta(c, f) {
            const base = this.cards[this.scBaseIdx]
            if (!base || base === c) return ''
            const d = deltaToBase(c.row[f.binding], base.row[f.binding])
            if (d.abs === null || d.abs === 0) return ''
            const m = this.scMetricOf(f)
            return fmtCompact(d.abs, { money: isMoneyMetric(m), sign: true }) + (d.pct !== null ? ` (${d.pct > 0 ? '+' : d.pct < 0 ? '−' : ''}${Math.abs(d.pct).toFixed(Math.abs(d.pct) >= 10 ? 0 : 1)}%)` : '')
        },
        scNotRecalc(c) { return notRecalculated(c.row) },
        scNoData(c) { return noData(c.row) },
        scSourceLabel(c) { return sourceLabel(c.row) },   // IE 2026 09 30 / ODOO-343432
        scSourceTip(c) { return sourceTip(c.row) },
        scZeroStock(c) { return zeroStock(c.row) },   // IE 2026 09 30 / ODOO-343432
        scShowApply(c) {
            return canShowApply(c.row, { readonly: this.store.user?.readonly === true, applyButton: this.attr?.apply_button !== false })
        },
        scApply(c) {
            if (!this.scShowApply(c)) return
            scStats.applies++
            this.store.executeStoreMethod(this, applyAction(c.row, this.store.activeWORKSPACE?.workspace || ''))
        },
        scClickWidget(f) {
            return this.isCards ? clickTarget(this.scMetricOf(f), this.attr) : ''
        },
        async scClick(c, f) {
            const w = this.scClickWidget(f)
            if (!w) return
            scStats.clicks++
            const pm = String(c.row?.pm_id || '')
            // отчёт — под моделью карточки: переключение модели тем же путём, что сценарии Бизнес-кейсов (store.clientTest_switchPm)
            if (pm && this.store.process_model_record?.id !== pm) {
                const ok = await this.store.clientTest_switchPm(pm)
                if (!ok) return
            }
            this.store.executeStoreMethod(this, openAction(w, this.attr))
        },
        content_Changed() {
            this.store.widget_attr_set(this)
            this.data = this.store.get_value(this.WIDGET.record, '_doc.data', []) || []
            this.tick++
        },
        bestBindingFor(f) {
            // IE 2026 08 16 / ODOO-4997: точное совпадение прежде вхождения —
            // иначе m10 находил best_m1
            if (this.bestFields[f.binding]) return this.bestFields[f.binding]
            // best_<suffix> относится к метрике, чей binding содержит suffix
            for (const suffix in this.bestFields) {
                if (suffix === 'score') continue
                if (f.binding.includes(suffix)) return this.bestFields[suffix]
            }
            return null
        },
        metricUniform(f) {
            // IE 2026 08 25 / ODOO-5884 (замечание Игоря «зачем флажки в Production, если он везде
            // нулевой»): по метрике, где у всех карточек ОДНО и то же значение (или значений нет
            // вовсе), градация бессмысленна — значки не рисуем
            const key = f.binding
            if (this._uniTick !== this.tick) { this._uniMap = {}; this._uniTick = this.tick }
            if (this._uniMap && this._uniMap[key] !== undefined) return this._uniMap[key]
            if (!this._uniMap) this._uniMap = {}
            const nums = []
            for (const c of (this.cards || [])) {
                if (c.isDelta || c.pct) continue  // Δ / Δ% — служебные карточки сравнения
                const v = c.row ? c.row[key] : undefined
                if (v === null || v === undefined || v === '') continue
                const n = Number(v)
                if (!Number.isNaN(n)) nums.push(n)
            }
            const uniform = !nums.length || nums.every(v => v === nums[0])
            this._uniMap[key] = uniform
            return uniform
        },
        flagOf(card, f) {
            // IE 2026 09 30 / ODOO-343432 (What-if Scorecard): лучшая по метрике — по Better when строки (ничьи — у всех лучших; пусто и «все равны» — без значков)
            if (this.isCards) {
                const set = this.scBest[f.binding]
                if (!set || !set.size) return ''
                const v = card.row[f.binding]
                if (v === undefined || v === null || v === '') return ''
                return set.has(this.cards.indexOf(card)) ? 'best' : 'worse'
            }
            const bb = this.bestBindingFor(f)
            if (!bb) return ''
            if (this.metricUniform(f)) return ''
            // IE 2026 08 16 / ODOO-4997 (вопрос Игоря): градация строки не
            // задана (better пуст) — best_* не посчитан, значки не рисуем;
            // раньше NaN давал «worse» (⚠) на ОБЕИХ сторонах
            const v = card.row[bb]
            if (v === undefined || v === null) return ''
            return Number(v) ? 'best' : 'worse'
        },
        // IE 2026 08 17 / ODOO-4997 (заказ Игоря): строка Comparison rows для
        // метрики — единообразно для всех схем Source: у синтетических метрик
        // m<i> строка берётся по индексу, у схемы List rows — из привязанной
        // строки настроек (_cmpRow)
        cmpRowOf(f) {
            if (f._cmpRow) return f._cmpRow
            const mm = /^m(\d+)$/.exec(f.binding)
            if (mm) {
                const src = this.cmpSource === 'prev_model' ? this.prevModel.rows : this.cmpRows
                return src[+mm[1]] || null
            }
            return null
        },
        weightOf(f) {
            const w = parseFloat(this.cmpRowOf(f)?.weight)
            return Number.isNaN(w) ? null : w
        },
        // IE 2026 08 17 / ODOO-4997 (заказ Игоря): приведение значения метрики
        // к взвешиванию (колонка Score value строки настроек):
        //   '' (Ratio to baseline) — отношение к базовой карточке ×100, при
        //       Better when = Lower — обратное (для натуральных и абсолютных
        //       величин); потолок 200%, идеальный ноль при lower — тоже 200%;
        //   'value'   — значение уже процент-балл (относительные величины);
        //   'inverse' — 100 − значение (процент ошибки → точность);
        //   'ignore'  — строка показывается, но в балл не входит
        normScore(r, v, vb) {
            if (v === null || v === undefined || Number.isNaN(v)) return null
            switch (r.score_value) {
                case 'ignore': return null
                case 'value': return v
                case 'inverse': return 100 - v
                default: {
                    if (vb === null || vb === undefined || Number.isNaN(vb)) return null
                    let ratio
                    if (r.better === 'lower') {
                        if (v < 0 || vb < 0) return null
                        ratio = v === 0 ? 2 : vb / v
                    } else {
                        if (!vb) return null
                        ratio = v / vb
                    }
                    if (!Number.isFinite(ratio) || ratio < 0) return null
                    return 100 * Math.min(2, ratio)
                }
            }
        },
        // IE 2026 08 18 / ODOO-4997 (заказ Игоря): порядок карточек слева направо
        // задаётся полем строки (Order by) — напр. date_from записи процессной
        // модели / версии. Пустые значения уходят вправо, равные сохраняют
        // порядок данных; даты сравниваются как строки, числа — как числа.
        orderCards(rows) {
            const binding = this.attr?.compare_order_by
            if (!binding || !rows.length) return rows
            const desc = !!this.attr?.compare_order_desc
            const key = (r) => {
                const v = r?.[binding]
                if (v === undefined || v === null || v === '') return null
                return typeof v === 'object' ? String(v.id ?? v.value ?? v.title ?? '') : v
            }
            const idx = new Map(rows.map((r, i) => [r, i]))
            return [...rows].sort((a, b) => {
                const ka = key(a), kb = key(b)
                if (ka === null || kb === null) {
                    if (ka === null && kb === null) return idx.get(a) - idx.get(b)
                    return ka === null ? 1 : -1
                }
                const na = typeof ka === 'number' ? ka : Number(ka)
                const nb = typeof kb === 'number' ? kb : Number(kb)
                let d = (!Number.isNaN(na) && !Number.isNaN(nb) && String(ka).trim() !== '' && String(kb).trim() !== ''
                    && !Number.isNaN(Number(String(ka).trim())) && !Number.isNaN(Number(String(kb).trim())))
                    ? na - nb
                    : String(ka).localeCompare(String(kb))
                if (!d) return idx.get(a) - idx.get(b)
                return desc ? -d : d
            })
        },
        // строка карточки = ТЕКУЩАЯ процессная модель: поле Current model field
        // держит id процессной модели (сверяется с моделью страницы) либо флаг 1/0
        isCurrentRow(row) {
            const binding = this.attr?.compare_current_field
            if (!binding || !row) return false
            const v = row[binding]
            if (v === undefined || v === null || v === '') return false
            const s = typeof v === 'object' ? String(v.id ?? v.value ?? '') : String(v)
            if (s === '1' || s.toLowerCase() === 'true') return true
            if (s === '0' || s.toLowerCase() === 'false') return false
            const pm = this.currentPM
            return !!pm && s === pm
        },
        applyWeightedScores(list) {
            const wr = this.metricFields
                .map(f => ({ f, r: this.cmpRowOf(f), w: null }))
                .map(x => ({ ...x, w: x.r ? parseFloat(x.r.weight) : NaN }))
                .filter(x => x.r && !Number.isNaN(x.w) && x.w > 0)
            if (!wr.length || !list.length) return
            const src = this.cmpSource
            const val = (card, f) => {
                const v = card.row[f.binding]
                if (typeof v === 'number') return v
                if (v === null || v === undefined || v === '') return null
                const p = parseFloat(v)
                return Number.isNaN(p) ? null : p
            }
            // базовая карточка: prev_model — Previous model; иначе — карточка
            // «…(baseline)» из данных, при отсутствии — первая.
            // IE 2026 08 17: карточки-ИЗМЕРЕНИЯ (dim, напр. по горизонтам) базы
            // не имеют — балл считается только по строкам с алгоритмами без
            // базы (Value as is / 100 − value)
            const isDim = src === 'prev_model' && !this.legacyPrevRows.length && !!this.prevModel.dim
            let baseIdx = 0
            if (src === 'prev_model') baseIdx = 1
            else {
                // IE 2026 08 18 / ODOO-4997: при распознанной ТЕКУЩЕЙ модели
                // (Current model field) базой Ratio служит она — иначе сортировка
                // Order by меняла бы базу (первую карточку) и весь Total Score
                const ci = list.findIndex(c => c.isCurrent)
                const bi = ci >= 0 ? ci : list.findIndex(c => /baseline/i.test(c.title))
                if (bi >= 0) baseIdx = bi
            }
            const base = isDim ? null : list[baseIdx]
            for (const c of list) {
                // prev_model: балл только у текущей модели (Δ/Δ% и базовая — без)
                if (src === 'prev_model' && !isDim && c !== list[0]) continue
                let acc = 0, wsum = 0
                for (const x of wr) {
                    const s = this.normScore(x.r, val(c, x.f), base ? val(base, x.f) : null)
                    if (s === null) continue
                    acc += x.w * s
                    wsum += x.w
                }
                if (wsum > 0) c.calcScore = acc / wsum
            }
        },
        // IE 2026 08 17 / ODOO-4997 (заказ Игоря): условное оформление ячеек
        // карточек — правила compare_cf настроек; ПУСТАЯ таблица = встроенные
        // правила (✓ у лучшей стороны, ⚠ у худшей), заполненная — полностью
        // заменяет их. Правила применяются сверху вниз, поздние переопределяют.
        cfEffectiveRules() {
            const rr = (this.attr?.compare_cf || []).filter(r => r && r.use !== false)
            if (rr.length) return rr
            return [
                { grade: 'best', icon: 'pi-check', icon_color: '#2e9e57' },
                { grade: 'worse', icon: 'pi-exclamation-triangle', icon_color: '#e8a33d' },
            ]
        },
        inHorizon(node, key) {
            // IE 2026 08 25 / ODOO-5884: ячейка принадлежит выбранному горизонту (supply_horizon ячейки);
            // если у ячейки горизонта нет — выводим его по дате против даты фактических остатков
            // (past/future), как это делает разрез карточек по горизонтам
            const h = this.horizonSel
            if (!h) return true
            const d = String(key).split('.')[0]
            const cell = node ? node[d] : null
            let v = cell && typeof cell === 'object' ? (cell.supply_horizon ?? cell.horizon) : undefined
            if ((v === undefined || v === null || v === '') && this.stockDate && /^d?\d{4}-\d{2}/.test(d)) {
                v = d.replace(/^d/, '').slice(0, 10) < this.stockDate ? 'past' : 'future'
            }
            return String(v || '') === h
        },
        measureAggOf(node) {
            // IE 2026 08 25 / ODOO-5884: правило свёртки ПО МЕРЕ строки (fields[].aggregateScale_measure:
            // open_balance → opening_balance) — тот же источник, что у грида
            const m = typeof node?.measure === 'string' ? node.measure : (node?.measure?.id || '')
            if (!m) return ''
            if (this._measAggTick !== this.tick) { this._measAggMap = null; this._measAggTick = this.tick }
            if (!this._measAggMap) {
                const map = {}
                const walkF = (fields) => {
                    for (const f of fields || []) {
                        if (!f || typeof f !== 'object') continue
                        const asm = f.aggregateScale_measure
                        if (asm && typeof asm === 'object') Object.assign(map, asm)
                        walkF(f.fields || f.attrs)
                    }
                }
                walkF(this.WIDGET?.record?.doc?.params?.fields)
                this._measAggMap = map
            }
            return this._measAggMap[m] || ''
        },
        dlAggOf(row_id) {
            // IE 2026 08 25 / ODOO-5884: эффективное правило свёртки строки data_layout2 по row_id
            // (наследуется от родителя — Stock: opening_balance), тот же порядок, что в AttrGrid
            if (!row_id) return ''
            if (this._dlAggTick !== this.tick) { this._dlAggMap = null; this._dlAggTick = this.tick }
            if (!this._dlAggMap) {
                const map = {}
                const walk = (rows, inherited) => {
                    for (const r0 of rows || []) {
                        if (!r0 || typeof r0 !== 'object') continue
                        const eff = r0.aggregateScale || r0.aggregate || inherited
                        if (r0.row_id) map[r0.row_id] = eff
                        walk(r0.attrs, eff)
                    }
                }
                walk(this.WIDGET?.record?.doc?.params?.data_layout2, undefined)
                this._dlAggMap = map
            }
            return this._dlAggMap[row_id] || ''
        },
        rowCF(f) {
            // IE 2026 08 25 / ODOO-5884 (заказ Игоря): цвет ЗАГОЛОВКА строки метрики — из правил
            // compare_cf, привязанных к метрике (без градации/условия на значение: они про ячейки)
            const out = {}
            for (const r of this.cfEffectiveRules()) {
                if (!r.metric || r.grade || r.condition || (r.WHERE || []).filter(w => w && w.use !== false).length) continue
                const m = String(r.metric).toLowerCase()
                if (!(((f.title || '') + ' ' + (f.binding || '')).toLowerCase().includes(m))) continue
                if (r.color) out.color = r.color
            }
            return out
        },
        cfIconClass(icon) {
            const s = String(icon)
            return s.startsWith('pi ') ? s : 'pi ' + s
        },
        cellCF(card, f) {
            const out = {}
            const vRaw = card.row[f.binding]
            const v = typeof vRaw === 'number' ? vRaw : parseFloat(vRaw)
            const grade = this.flagOf(card, f)
            for (const r of this.cfEffectiveRules()) {
                if (r.metric) {
                    const m = String(r.metric).toLowerCase()
                    if (!(((f.title || '') + ' ' + (f.binding || '')).toLowerCase().includes(m))) continue
                }
                if (r.grade && r.grade !== grade) continue
                if (r.condition) {
                    if (Number.isNaN(v)) continue
                    if (r.condition === 'lt0' && !(v < 0)) continue
                    if (r.condition === 'gt0' && !(v > 0)) continue
                    if (r.condition === 'eq0' && v !== 0) continue
                    if (r.condition === 'ne0' && v === 0) continue
                }
                const WHERE = (r.WHERE || []).filter(w => w && w.use !== false)
                if (WHERE.length) {
                    const ctx = { value: Number.isNaN(v) ? null : v, metric: f.title, binding: f.binding,
                        grade, card: card.title, weight: this.weightOf(f) }
                    if (!cfCheckWHERE({ WHERE }, ctx, null, null, { getValue: (o, b) => this.store.get_value(o, b) })) continue
                }
                for (const k of ['color', 'background', 'icon', 'icon_color']) if (r[k]) out[k] = r[k]
            }
            return out
        },
        scoreOf(c) {
            if (c.calcScore != null) return c.calcScore
            if (this.scoreField) {
                const v = c.row[this.scoreField.binding]
                return v === null || v === undefined || v === '' ? null : v
            }
            return null
        },
        fmtWeight(w) {
            if (w === null || w === undefined) return ''
            return w.toLocaleString('en-US', { maximumFractionDigits: 1 }) + '%'
        },
        fmt(v, card) {
            if (v === null || v === undefined || v === '') return ''
            if (typeof v !== 'number') return String(v)
            // IE 2026 08 16 / ODOO-4997: карточка Δ% схемы prev_model (pct лежит
            // на строке данных карточки, обёртка cards() его не копирует)
            const pct = !!(card && (card.pct || card.row?.pct))
            // IE 2026 08 16 / ODOO-4997 (заказ Игоря): у крупных значений дробная
            // часть не показывается — числа >100 и проценты >5%
            const digits = Math.abs(v) > (pct ? 5 : 100) ? 0 : 1
            let s = v.toLocaleString('en-US', { maximumFractionDigits: digits })
            if (s === '-0') s = '0' // округлённый «-0%» у нулевых отклонений
            return pct ? s + '%' : s
        },
        // IE 2026 08 16 / ODOO-4997 (заказ Игоря): помощники схемы prev_model
        rowLabel(n) {
            // метка строки отчёта — как label_key движка (data_layout2)
            for (const k of ['measure_type', 'measure', 'title', 'name']) {
                const v = n[k]
                if (typeof v === 'string' && v) return v
            }
            for (const k in n) {
                if (k.startsWith('_') || k === 'attrs') continue
                const v = n[k]
                if (typeof v === 'string' && v && !/^[0-9a-f]{8}-[0-9a-f]{4}-/i.test(v)) return v
            }
            return ''
        },
        metricTitle(node) {
            // IE 2026 08 16 / ODOO-4997 (заказ Игоря): титул меры — как в строке
            // лэйаута: measure_type/measure + отличающееся строковое измерение
            // (напр. 'Distr IN' + 'Uncovered'); GUID-ссылки не берём
            const parts = []
            for (const k of ['measure_type', 'measure', 'alert', 'title', 'name']) {
                const v = node[k]
                if (typeof v === 'string' && v && !parts.includes(v)
                    && !/^[0-9a-f]{8}-[0-9a-f]{4}-/i.test(v)) parts.push(v)
                if (parts.length >= 2) break
            }
            return parts.join(' ') || this.rowLabel(node)
        },
        dlTitleOf(node) {
            // IE 2026 08 19 / ODOO-5512 (скрин Игоря «пустые цифры в карточках»):
            // титул ВЕТКИ лэйаута строки (Sales / Purch / Shipment 926 / Inventory)
            // по _DLRow.row_id — строки настроек сравнения в Purchase названы
            // титулами веток DL, а metricTitle собирал 'OrderSupplierShipment
            // IN_shipped' и ни одна строка не совпадала (все карточки пустые)
            const rid = node?._DLRow?.row_id
            if (!rid) return null
            // кэш на один проход рендера: лэйаут мог поменяться между пересчётами
            if (this._dlTitleTick !== this.tick) { this._dlTitleMap = null; this._dlTitleTick = this.tick }
            if (!this._dlTitleMap) {
                const m = {}
                const dl2 = this.store.get_value(this.WIDGET?.record, 'doc.params.data_layout2') || []
                const walk = (rows, branch) => {
                    for (const r0 of rows || []) {
                        if (!r0 || typeof r0 !== 'object') continue
                        const b = branch || (typeof r0.title === 'string' && r0.title.trim() ? r0.title.trim() : null)
                        if (r0.row_id) m[r0.row_id] = b
                        walk(r0.attrs, b)
                    }
                }
                for (const root of dl2) walk(root?.attrs, null)
                this._dlTitleMap = m
            }
            return this._dlTitleMap[rid] || null
        },
        titleMatches(l, title) {
            if (this.metricTitle(l.metric) === title) return true
            const bt = this.dlTitleOf(l.metric) || this.dlTitleOf(l.n)
            return !!bt && bt === title
        },
        leafMatches(l, r) {
            if (r._autoLabel !== undefined) return this.titleMatches(l, r._autoLabel)
            const WHERE = (r.WHERE || []).filter(w => w && w.use !== false)
            if (!WHERE.length) {
                // IE 2026 08 17 / ODOO-4997 (заказ Игоря): без отбора строка
                // настроек сопоставляется по Title с титулом меры ИЛИ титулом
                // ветки лэйаута (Sales / Purch / Shipment 926 …)
                if (r.title) return this.titleMatches(l, r.title)
                return true
            }
            return cfCheckWHERE({ WHERE }, l.n, null, null, { getValue: (o, b) => this.store.get_value(o, b) })
        },
        cellNums(row, out = {}) {
            // IE 2026 08 16 / ODOO-4997: агрегируем ТОЛЬКО контейнерные ячейки
            // (колонки-периоды {поле: число}); плоские числа строки — измерения
            // (level, horizon_order) и загрязняли суммы/Min
            for (const k in row) {
                if (k.startsWith('_') || k === 'attrs') continue
                const v = row[k]
                if (v && typeof v === 'object' && !Array.isArray(v)) this.flatNums(v, k + '.', out)
            }
            return out
        },
        dimensionCards(leaves, rows) {
            // IE 2026 08 16 / ODOO-4997 (заказ Игоря): карточки по значениям
            // измерений (Card fields), напр. по горизонтам; сортировка по
            // Order by (horizon_order), затем по значению
            const cardFields = (this.attr?.card_fields || []).filter(f => f && f.use !== false && f.binding)
            if (!cardFields.length) return null
            const combos = new Map()
            for (const l of leaves) {
                const vals = cardFields.map(f => {
                    const v = l.n[f.binding]
                    return v === undefined || v === null ? '' : String(v)
                })
                if (vals.every(v => v === '')) continue
                const key = vals.join('\u0001')
                if (!combos.has(key)) {
                    combos.set(key, { vals, ord: cardFields.map(f => f.order_by ? l.n[f.order_by] : null), leaves: [] })
                }
                combos.get(key).leaves.push(l)
            }
            // IE 2026 08 16 / ODOO-4997 (заказ Игоря): поле не найдено на СТРОКАХ —
            // пробуем разрез по полю ЯЧЕЕК-дат (напр. supply_horizon в D&S):
            // карточка = значение поля, в неё агрегируются только её ячейки
            if (!combos.size) {
                const f0 = cardFields[0]
                // IE 2026 08 20 / ODOO-5512 (заказ Игоря): у отчётов без
                // supply_horizon на ячейках (напр. Sales) горизонт выводится по
                // дате колонки против date_fact_stock модели окружения:
                // прошлое = Past, остальное = Future; нет даты факта — как раньше
                const _pmDates = this.store?.process_model_record?.doc?.params?.dates || {}
                const _factDate = String(_pmDates['purchase_date_fact_stock']
                    || _pmDates['sales-date_fact_stock'] || _pmDates['distribution_date_fact_stock'] || '').slice(0, 10)
                const map = new Map()
                for (const l of leaves) {
                    for (const k of Object.keys(l.n).sort()) {
                        if (k.startsWith('_') || k === 'attrs') continue
                        const cell = l.n[k]
                        // IE 2026 08 20 / ODOO-5512: колонки-периоды бывают и ПЛОСКИМИ
                        // числами (Sales) — для вывода горизонта по дате они тоже ячейки
                        const plain = typeof cell === 'number' && isFinite(cell)
                        if (!plain && (!cell || typeof cell !== 'object' || Array.isArray(cell))) continue
                        let dv = plain ? undefined : cell[f0.binding]
                        if ((dv === undefined || dv === null || dv === '')
                            && f0.binding === 'supply_horizon' && _factDate && /^d?\d{4}-\d{2}/.test(k)) {
                            dv = k.replace(/^d/, '').slice(0, 10) < _factDate ? 'past' : 'future'
                        }
                        if (dv === undefined || dv === null || dv === '') continue
                        let e = map.get(String(dv))
                        if (!e) { e = { val: String(dv), cells: [] }; map.set(String(dv), e) }
                        e.cells.push({ l, key: k, cell: plain ? { value: cell } : cell })
                    }
                }
                if (!map.size) return null
                const HOR_ORDER = { past: 1, lt: 2, rc: 3, rc1: 4, future: 5 }
                const HOR_TITLES = { past: this.t('Past'), lt: this.t('Lead time'), rc: this.t('Replenishment cycle'), rc1: this.t('Next replenishment cycle'), future: this.t('Future') }   // IE 2026 09 23 / ODOO-341407: i18n
                const list = [...map.values()].sort((a, b) =>
                    (HOR_ORDER[a.val] ?? 99) - (HOR_ORDER[b.val] ?? 99) || (a.val < b.val ? -1 : 1))
                const cards = list.map(e => {
                    const card = { title: HOR_TITLES[e.val] || e.val }
                    rows.forEach((r, i) => {
                        const vals = []
                        // IE 2026 08 25 / ODOO-5884 (заказ Игоря): правило свёртки строки/меры («остаток на
                        // начало периода» — opening_balance) действует и в карточках по горизонтам:
                        // такие меры не суммируются по датам, берётся первое (последнее) значение периода
                        const groups = new Map()
                        for (const ce of e.cells) {
                            if (!this.leafMatches(ce.l, r)) continue
                            if (!this.inPeriod(ce.key, r)) continue
                            if (!this.inHorizon(ce.l.n, ce.key)) continue
                            const rule0 = String(r.aggregate || this.measureAggOf(ce.l.n) || this.dlAggOf(ce.l.n?._DLRow?.row_id) || '')
                            const edge0 = /^(First|opening_balance)$/i.test(rule0) ? 'first'
                                : /^(Last|closing_balance)$/i.test(rule0) ? 'last' : ''
                            const nums = this.flatNums(ce.cell, ce.key + '.')
                            if (!edge0) { for (const k2 in nums) vals.push(nums[k2]); continue }
                            for (const k2 in nums) {
                                const dot = String(k2).indexOf('.')
                                const fld = dot > 0 ? String(k2).slice(dot + 1) : ''
                                const gk = (ce.l.key || ce.l.n?._DLRow?.row_id || '') + '\u0001' + (ce.l.n?.subject || '') + '\u0001' + fld + '\u0001' + edge0
                                const arr = groups.get(gk) || []
                                arr.push([String(ce.key), nums[k2], edge0])
                                groups.set(gk, arr)
                            }
                        }
                        for (const arr of groups.values()) {
                            arr.sort((x, y) => x[0] < y[0] ? -1 : x[0] > y[0] ? 1 : 0)
                            const pick = arr[0][2] === 'first' ? arr[0] : arr[arr.length - 1]
                            vals.push(pick[1])
                        }
                        card['m' + i] = this.aggVals(vals, r.aggregate)
                    })
                    return card
                })
                return { cards, rows, dim: true }
            }
            const num = v => { const f = parseFloat(v); return Number.isNaN(f) ? null : f }
            const list = [...combos.values()].sort((a, b) => {
                for (let i = 0; i < cardFields.length; i++) {
                    const ax = a.ord[i] ?? a.vals[i], bx = b.ord[i] ?? b.vals[i]
                    const an = num(ax), bn = num(bx)
                    if (an !== null && bn !== null) { if (an !== bn) return an - bn }
                    else if (String(ax) !== String(bx)) return String(ax) < String(bx) ? -1 : 1
                }
                return 0
            })
            const cards = list.map(cb => {
                const card = { title: cb.vals.filter(Boolean).join(' / ') }
                rows.forEach((r, i) => {
                    const vals = []
                    for (const l of cb.leaves) {
                        if (!this.leafMatches(l, r)) continue
                        const cur = this.cellNums(l.n)
                        for (const k in cur) { if (this.inPeriod(k, r)) vals.push(cur[k]) }
                    }
                    card['m' + i] = this.aggVals(vals, r.aggregate)
                })
                return card
            })
            return { cards, rows, dim: true }
        },
        flatNums(row, pref = '', out = {}) {
            // плоские числовые значения строки: колонки-периоды 'YYYY-MM-DD' —
            // словари {поле: число} (бывают вложенными); служебные '_…'
            // (включая итоги '_total::') не считаем — двойной счёт с периодами
            for (const k in row) {
                if (k.startsWith('_') || k === 'attrs') continue
                const v = row[k]
                if (typeof v === 'number') out[pref + k] = v
                else if (v && typeof v === 'object' && !Array.isArray(v)) this.flatNums(v, pref + k + '.', out)
            }
            return out
        },
        inPeriod(key, r) {
            // границы периода — префикс ключа колонки ('2026-03' накрывает март)
            const base = String(key).split('.')[0]
            const from = r.date_from && String(r.date_from).trim()
            const to = r.date_to && String(r.date_to).trim()
            if (from && base.slice(0, from.length) < from) return false
            if (to && base.slice(0, to.length) > to) return false
            // IE 2026 08 25 / ODOO-5884 (заказ Игоря): общий период вкладки (от даты фактических
            // остатков) сужает строки дополнительно к их собственным Date from / Date to
            const pr = this.periodRange
            if (pr) {
                if (pr.from && base < pr.from) return false
                if (pr.to && base > pr.to) return false
            }
            return true
        },
        aggVals(vals, how) {
            if (!vals.length) return null
            switch (how || 'Sum') {
                case 'Avg': return vals.reduce((x, y) => x + y, 0) / vals.length
                case 'Min': return Math.min(...vals)
                case 'Max': return Math.max(...vals)
                default: return vals.reduce((x, y) => x + y, 0)
            }
        },
    },
}
</script>

<style scoped>
/* IE 2026 08 17 / ODOO-4997 (заказ Игоря): оформление карточек сравнения по
   макету — светлый фон панели, белая колонка названий метрик, значки статуса
   по ЛЕВОМУ краю ячейки, закрытая карточка со скруглением и нижней полосой.
   IE 2026 08 17 (заказ Игоря): палитра — из активной цветовой схемы
   (--cs-* с body.cscheme-*, см. App.vue); фолбэки = прежний синий вид
   для схемы Classic, у которой переменные не заданы */
.attr-compare0 {
    --cmp-bg: var(--cs-info-bg, #eaf3fc);
    --cmp-card-bg: #fff;
    --cmp-border: var(--cs-grid-border, #dbe4ef);
    --cmp-row-border: var(--cs-cell-border, #eef2f7);
    --cmp-accent: var(--cs-accent, #14509b);
    --cmp-title: var(--cs-title, #14509b);
    --cmp-primary-bg: var(--cs-badge-bg, #14509b);
    --cmp-primary-fg: var(--cs-badge-fg, #fff);
    --cmp-first-cell-bg: var(--cs-group1-bg, #f2f7fd);
    position: relative; width: 100%; height: 100%; overflow: auto;
    padding: 20px 16px 24px;
    background: var(--cmp-bg);
    box-sizing: border-box;
}
.attr-compare-empty { color: #8a949e; padding: 24px; text-align: center; }
.attr-compare-hint { color: #8a949e; padding: 10px 0 4px; text-align: center; font-size: 0.85rem; }
.attr-compare-tbl { border-collapse: separate; border-spacing: 14px 0; margin: 0 auto; }
.attr-compare-toprow td { height: 26px; }
.attr-compare-best-label { text-align: center; color: var(--cmp-title); font-weight: 700; font-size: 0.95rem; padding-bottom: 6px; }

.attr-compare-card-head {
    min-width: 190px; padding: 16px 18px 12px; text-align: center;
    border-radius: 10px 10px 0 0; background: var(--cmp-card-bg);
    box-shadow: 0 0 0 1px var(--cmp-border);
}
.attr-compare-card-head.is-base { background: var(--cmp-primary-bg); box-shadow: 0 0 0 1px var(--cmp-primary-bg); }
.attr-compare-card-head.is-base .attr-compare-title,
.attr-compare-card-head.is-base .attr-compare-score,
.attr-compare-card-head.is-base .attr-compare-score-unit,
.attr-compare-card-head.is-base .attr-compare-score-sub { color: var(--cmp-primary-fg); }
.attr-compare-card-head.is-best { box-shadow: 0 0 0 1px var(--cmp-border), inset 0 5px 0 var(--cmp-accent); }
.attr-compare-title { font-weight: 700; margin-bottom: 6px; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; max-width: 260px; color: var(--cmp-title); }
.attr-compare-score { font-size: 2.3rem; font-weight: 600; line-height: 1.1; color: #223; }
.attr-compare-score-unit { font-size: 1.1rem; color: #667; }
.attr-compare-score-sub { font-size: 0.78rem; color: #9aa5b1; }

/* колонка названий метрик — своя белая карточка слева.
   IE 2026 08 17 (заказ Игоря): блок ПРИЖАТ к первой карточке — фон
   продлевается псевдоэлементом через межколоночный зазор таблицы (14px),
   закругления справа нет */
.attr-compare-period {  /* IE 2026 08 25 / ODOO-5884: выбранный период карточек виден на листе */
    font-size: 12px; font-weight: 600; color: #5b6b80; padding: 2px 4px 8px 4px; white-space: nowrap;
}
.attr-compare-metric-name {
    padding: 12px 16px; font-weight: 600; white-space: nowrap; color: #33404d;
    background: var(--cmp-card-bg);
    position: relative;
}
.attr-compare-metric-name::after {
    content: ''; position: absolute; left: 100%; top: -1px; bottom: 0; width: 14px;
    background: inherit; border-top: inherit; box-shadow: inherit;
}
.attr-compare-tbl tr:nth-child(3) .attr-compare-metric-name { border-radius: 10px 0 0 0; }
/* IE 2026 08 17: блок названий метрик закрывается на ПОСЛЕДНЕЙ строке метрик
   (перед footrow) — карточки «выносятся» ниже него, как на макете */
.attr-compare-tbl tr:nth-last-child(2) .attr-compare-metric-name {
    border-radius: 0 0 0 10px;
    box-shadow: inset 0 -1px 0 var(--cmp-row-border);
}
.attr-compare-metrics-head, .attr-compare-metrics-foot { background: transparent; }

/* строка значений: значок статуса слева, число справа */
.attr-compare-cell {
    padding: 12px 16px; white-space: nowrap; background: var(--cmp-card-bg);
    box-shadow: inset 1px 0 0 var(--cmp-border), inset -1px 0 0 var(--cmp-border);
}
.attr-compare-cell.is-base { background: var(--cmp-first-cell-bg); }
.attr-compare-cellbox { display: flex; align-items: center; justify-content: space-between; gap: 10px; }
.attr-compare-flag { flex: 0 0 auto; width: 1.2rem; text-align: left; }
.attr-compare-flag-best { color: #2e9e57; }
.attr-compare-flag-worse { color: #e8a33d; }
.attr-compare-value { flex: 1 1 auto; text-align: right; font-variant-numeric: tabular-nums; font-weight: 600; color: #26313c; }
.attr-compare-cell.is-base .attr-compare-value { color: var(--cmp-title); }

/* IE 2026 08 17 (заказ Игоря): «вынос» — нижняя полоса карточки ниже блока
   названий метрик, со скруглением и рамкой; у лучшей — цветная полоса */
.attr-compare-footrow td { height: 22px; }
.attr-compare-card-foot {
    background: var(--cmp-card-bg);
    border-radius: 0 0 10px 10px;
    box-shadow: inset 1px 0 0 var(--cmp-border), inset -1px 0 0 var(--cmp-border), inset 0 -1px 0 var(--cmp-border);
}
.attr-compare-card-foot.is-base { background: var(--cmp-primary-bg); box-shadow: none; }
.attr-compare-card-foot.is-best {
    box-shadow: inset 1px 0 0 var(--cmp-border), inset -1px 0 0 var(--cmp-border), inset 0 -6px 0 var(--cmp-accent);
}

/* IE 2026 08 17 (заказ Игоря): колонка весов метрик справа от карточек —
   прижата ВЛЕВО к последней карточке (сдвиг на межколоночный зазор 14px),
   со своей светлой подложкой-панелью как на макете. Подпись Weight стоит НАД
   подложкой (без фона), скругления — только справа: слева блок прижат вплотную */
.attr-compare-weight-head { vertical-align: bottom; padding: 0; position: relative; left: -14px; }
.attr-compare-weight-title {
    color: #9aa5b1; font-size: 0.8rem; text-align: left;
    background: transparent;
    padding: 10px 10px 12px 8px;
}
.attr-compare-weight-cell {
    padding: 12px 10px 12px 8px; text-align: left; white-space: nowrap;
    color: #9aa5b1; font-variant-numeric: tabular-nums;
    position: relative; left: -14px;
    background: rgba(255,255,255,0.55);
}
/* верх подложки — от ПЕРВОЙ строки значений, низ — на последней строке метрик
   (карточки выносятся ниже); слева скруглений нет */
.attr-compare-tbl tr:nth-child(3) .attr-compare-weight-cell { border-radius: 0 10px 0 0; }
.attr-compare-tbl tr:nth-last-child(2) .attr-compare-weight-cell { border-radius: 0 0 10px 0; }
.attr-compare-weight-foot { background: transparent; }

/* IE 2026 09 30 / ODOO-343432 (What-if Scorecard): карточный вид — крупные числа, Δ к базе под числом, вердикт, бейдж, Apply */
.attr-compare-verdict { font-size: 0.95rem; font-weight: 600; color: var(--cmp-title); padding: 0 4px 10px 4px; }
.attr-compare0.is-cards .attr-compare-sc-value { font-size: 1.35rem; line-height: 1.15; display: inline-flex; flex-direction: column; align-items: flex-end; }
.attr-compare-sc-value.is-click { cursor: pointer; text-decoration: underline dotted; text-underline-offset: 4px; }
.attr-compare-sc-value.is-click:hover { color: var(--cmp-accent) !important; }
.attr-compare-sc-delta { font-size: 0.72rem; font-weight: 500; color: #7a8694; text-decoration: none; }
.attr-compare-sc-badge {
    display: inline-block; margin: 0 0 6px; padding: 1px 8px; border-radius: 10px;
    font-size: 0.72rem; font-weight: 600; background: #fff4e0; color: #9a5b00;
}
.attr-compare-sc-badge.is-none { background: #f1f3f5; color: #6c757d; }
.attr-compare-sc-badge.is-src { background: #eef4fb; color: #3d5a80; font-weight: 500; margin-left: 4px; cursor: help; }   /* IE 2026 09 30 / ODOO-343432 */
.attr-compare-apply {
    margin-top: 8px; padding: 3px 10px; border-radius: 6px; cursor: pointer; font-size: 0.8rem; font-weight: 600;
    border: 1px solid var(--cmp-accent); background: transparent; color: var(--cmp-accent);
}
.attr-compare-card-head.is-base .attr-compare-apply { border-color: var(--cmp-primary-fg); color: var(--cmp-primary-fg); }
.attr-compare-apply:hover { background: var(--cmp-accent); color: #fff; }

/* разделители строк внутри карточек */
.attr-compare-tbl tr + tr .attr-compare-cell,
.attr-compare-tbl tr + tr .attr-compare-metric-name { border-top: 1px solid var(--cmp-row-border); }
.attr-compare-footrow .attr-compare-card-foot { border-top: 1px solid var(--cmp-row-border); }
.attr-compare-footrow .attr-compare-card-foot.is-base { border-top: none; }
</style>
